import {
  type UseMutateAsyncFunction,
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysSite } from './queryKeys';

type CostCenterIds = UUID[];

type MutationCostCenterArguments = {
  siteId: UUID;
  costCenterIds: CostCenterIds;
};

type MutationCostCenterArgumentsResponse = {
  ids: CostCenterIds;
};

type UpdateSiteCostCentersMutationOptions = UseMutationOptions<
  MutationCostCenterArgumentsResponse,
  Error,
  MutationCostCenterArguments
>;

export type UpdateSiteCostCentersMutateAsyncFunction = UseMutateAsyncFunction<
  MutationCostCenterArgumentsResponse,
  Error,
  MutationCostCenterArguments
>;

/**
 * Updates the cost centers (aka accounting references) of a given site.
 *
 * @param {string} siteId - The ID of the site to update.
 * @param {string[]} costCenterIds - Array of cost center IDs to assign to the site.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Site/operation/bulk_add_cost_center_to_site_site__site_uuid__accounting_reference_post
 */
export const updateSiteCostCenters = async (
  siteId: UUID,
  costCenterIds: CostCenterIds,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.SITE.UPDATE_COST_CENTERS(siteId), {
      json: { ids: costCenterIds },
    })
    .json<MutationCostCenterArgumentsResponse>();

  return response;
};

/**
 * Custom hook for updating the cost centers (aka accounting references) of a given site.
 *
 * @returns {Object} An object containing the mutation function and related properties.
 */
export const useMutationUpdateSiteCostCenters = (
  options?: UpdateSiteCostCentersMutationOptions,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      siteId,
      costCenterIds,
    }: MutationCostCenterArguments) =>
      updateSiteCostCenters(siteId, costCenterIds),
    onSuccess(_, { siteId }) {
      queryClient.invalidateQueries({
        queryKey: queryKeysSite.get(siteId),
      });
    },
    ...options,
  });
};
